import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { httpsCallable } from "firebase/functions";
import { signInWithCustomToken, onAuthStateChanged } from "firebase/auth";
import { functions, auth } from "../firebase/config";
import "./AdminLogin.css";

const ALLOWED_DOMAINS = ['titanium6.com', 'stampmedical.com', 'chirofitgroup.com'];
const REMEMBER_DEVICE_DAYS = 30;

// Generate a unique device ID
const generateDeviceId = () => {
    return 'device_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
};

const AdminLogin = ({ onLogin }) => {
    const [step, setStep] = useState(1); // 1: email, 2: code, 3: password
    const [email, setEmail] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [credentials, setCredentials] = useState({
        username: "",
        password: "",
    });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [codeExpiry, setCodeExpiry] = useState(null);
    const [rememberDevice, setRememberDevice] = useState(true);
    const [isRememberedDevice, setIsRememberedDevice] = useState(false);
    const [customToken, setCustomToken] = useState(null);
    const [firebaseUser, setFirebaseUser] = useState(null);

    // Track Firebase Auth state
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => {
            setFirebaseUser(user);
        });
        return () => unsub();
    }, []);

    // Check for remembered device on mount and refresh the auth token
    useEffect(() => {
        const savedEmail = localStorage.getItem("admin_remembered_email");
        const savedDeviceId = localStorage.getItem("admin_device_id");
        const savedExpiry = localStorage.getItem("admin_device_expiry");

        if (savedEmail && savedDeviceId && savedExpiry) {
            const expiryDate = new Date(parseInt(savedExpiry));
            if (expiryDate > new Date()) {
                setEmail(savedEmail);
                setIsRememberedDevice(true);
                setStep(3);
                setSuccess(`Welcome back! Device remembered for ${savedEmail}`);

                const refreshToken = httpsCallable(functions, 'refreshAdminToken');
                refreshToken({ email: savedEmail, deviceId: savedDeviceId })
                    .then((result) => {
                        if (result.data.customToken) {
                            setCustomToken(result.data.customToken);
                        }
                    })
                    .catch((err) => {
                        console.warn("Device trust expired or invalid, will require full MFA:", err.message);
                        localStorage.removeItem("admin_remembered_email");
                        localStorage.removeItem("admin_device_id");
                        localStorage.removeItem("admin_device_expiry");
                        setIsRememberedDevice(false);
                        setEmail("");
                        setStep(1);
                        setSuccess("");
                        setError("Session expired. Please verify your email again.");
                    });
            } else {
                localStorage.removeItem("admin_remembered_email");
                localStorage.removeItem("admin_device_id");
                localStorage.removeItem("admin_device_expiry");
            }
        }
    }, []);

    // Validate email domain
    const isValidDomain = (email) => {
        const domain = email.split('@')[1]?.toLowerCase();
        return ALLOWED_DOMAINS.includes(domain);
    };

    // Handle email submission (Step 1)
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!email || !email.includes('@')) {
            setError("Please enter a valid email address");
            return;
        }

        if (!isValidDomain(email)) {
            setError("Access restricted to @titanium6.com and @stampmedical.com email addresses only");
            return;
        }

        setIsLoading(true);

        try {
            const sendCode = httpsCallable(functions, 'sendAdminVerificationCode');
            const result = await sendCode({ email: email.toLowerCase() });

            if (result.data.success) {
                setSuccess(`Verification code sent to ${email}`);
                setCodeExpiry(Date.now() + 5 * 60 * 1000); // 5 minutes
                setStep(2);
            }
        } catch (err) {
            console.error("Error sending verification code:", err);
            setError(err.message || "Failed to send verification code. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

// Handle code verification (Step 2)
    const handleCodeSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!verificationCode || verificationCode.length !== 6) {
            setError("Please enter the 6-digit verification code");
            return;
        }

        if (codeExpiry && Date.now() > codeExpiry) {
            setError("Verification code has expired. Please request a new one.");
            setStep(1);
            setVerificationCode("");
            return;
        }

        setIsLoading(true);

        try {
            const deviceId = rememberDevice ? generateDeviceId() : null;
            const verifyCode = httpsCallable(functions, 'verifyAdminCode');
            const result = await verifyCode({
                email: email.toLowerCase(),
                code: verificationCode,
                deviceId,
                rememberDevice,
            });

            if (result.data.success) {
                setCustomToken(result.data.customToken);

                if (rememberDevice && deviceId) {
                    const expiryDate = Date.now() + (REMEMBER_DEVICE_DAYS * 24 * 60 * 60 * 1000);
                    localStorage.setItem("admin_remembered_email", email.toLowerCase());
                    localStorage.setItem("admin_device_id", deviceId);
                    localStorage.setItem("admin_device_expiry", expiryDate.toString());
                }

                setSuccess("Email verified! Please enter your password.");
                setStep(3);
            }
        } catch (err) {
            console.error("Error verifying code:", err);
            setError(err.message || "Invalid verification code. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Handle password submission (Step 3)
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");

        await new Promise((resolve) => setTimeout(resolve, 500));

        if (credentials.password === "titanium@2025!") {
            try {
                if (!firebaseUser) {
                    if (customToken) {
                        await signInWithCustomToken(auth, customToken);
                    } else {
                        setError("Session expired. Please complete full verification.");
                        handleForgetDevice();
                        setIsLoading(false);
                        return;
                    }
                }

                localStorage.setItem("admin_authenticated", "true");
                localStorage.setItem("admin_email", email);
                localStorage.setItem("admin_auth_time", Date.now().toString());
                onLogin();
            } catch (authError) {
                console.error("Firebase Auth sign-in error:", authError);
                setError("Authentication failed. Please try the full verification again.");
                handleForgetDevice();
            }
        } else {
            setError("Invalid password");
        }

        setIsLoading(false);
    };

    // Resend verification code
    const handleResendCode = async () => {
        setError("");
        setSuccess("");
        setIsLoading(true);

        try {
            const sendCode = httpsCallable(functions, 'sendAdminVerificationCode');
            const result = await sendCode({ email: email.toLowerCase() });

            if (result.data.success) {
                setSuccess(`New verification code sent to ${email}`);
                setCodeExpiry(Date.now() + 5 * 60 * 1000);
                setVerificationCode("");
            }
        } catch (err) {
            console.error("Error resending code:", err);
            setError("Failed to resend code. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Go back to email step
    const handleBackToEmail = () => {
        setStep(1);
        setVerificationCode("");
        setError("");
        setSuccess("");
        setIsRememberedDevice(false);
    };

    // Forget this device
    const handleForgetDevice = () => {
        localStorage.removeItem("admin_remembered_email");
        localStorage.removeItem("admin_device_id");
        localStorage.removeItem("admin_device_expiry");
        setIsRememberedDevice(false);
        setEmail("");
        setStep(1);
        setSuccess("");
        setError("");
    };

    return (
        <div className="admin-login-container">
            <motion.div
                className="admin-login-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}>
                <div className="admin-header">
                    <h1>Stamp Medical Admin</h1>
                    <p className="step-indicator">
                        {step === 1 && "Step 1 of 3: Email Verification"}
                        {step === 2 && "Step 2 of 3: Enter Code"}
                        {step === 3 && "Step 3 of 3: Password"}
                    </p>
                </div>

                {/* Step 1: Email */}
                {step === 1 && (
                    <form onSubmit={handleEmailSubmit} className="login-form">
                        <div className="form-group">
                            <label htmlFor="email">Work Email</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                                required
                                placeholder="you@titanium6.com"
                                autoComplete="email"
                            />
                            <p className="input-hint">
                                Only @titanium6.com and @stampmedical.com emails are allowed
                            </p>
                        </div>

                        {error && (
                            <motion.div
                                className="error-message"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}>
                                {error}
                            </motion.div>
                        )}

                        <button
                            type="submit"
                            className="login-btn"
                            disabled={isLoading}>
                            {isLoading ? "Sending Code..." : "Send Verification Code"}
                        </button>
                    </form>
                )}

                {/* Step 2: Verification Code */}
                {step === 2 && (
                    <form onSubmit={handleCodeSubmit} className="login-form">
                        <div className="email-display">
                            <span>📧</span>
                            <span>{email}</span>
                            <button type="button" onClick={handleBackToEmail} className="change-email-btn">
                                Change
                            </button>
                        </div>

                        <div className="form-group">
                            <label htmlFor="code">Verification Code</label>
                            <input
                                type="text"
                                id="code"
                                value={verificationCode}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                    setVerificationCode(val);
                                    setError("");
                                }}
                                required
                                placeholder="Enter 6-digit code"
                                className="code-input"
                                autoComplete="one-time-code"
                                inputMode="numeric"
                            />
                            <p className="input-hint">
                                Check your email for the verification code (expires in 5 minutes)
                            </p>
                        </div>

                        {success && (
                            <motion.div
                                className="success-message"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}>
                                {success}
                            </motion.div>
                        )}

                        {error && (
                            <motion.div
                                className="error-message"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}>
                                {error}
                            </motion.div>
                        )}

                        <div className="remember-device-checkbox">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={rememberDevice}
                                    onChange={(e) => setRememberDevice(e.target.checked)}
                                />
                                <span>Remember this device for 30 days</span>
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="login-btn"
                            disabled={isLoading || verificationCode.length !== 6}>
                            {isLoading ? "Verifying..." : "Verify Code"}
                        </button>

                        <button
                            type="button"
                            onClick={handleResendCode}
                            className="resend-btn"
                            disabled={isLoading}>
                            Resend Code
                        </button>
                    </form>
                )}

                {/* Step 3: Password */}
                {step === 3 && (
                    <form onSubmit={handlePasswordSubmit} className="login-form">
                        <div className="verified-email-display">
                            <span>{isRememberedDevice ? '🔐' : '✅'}</span>
                            <span>{email} {isRememberedDevice ? '(remembered)' : 'verified'}</span>
                            {isRememberedDevice && (
                                <button type="button" onClick={handleForgetDevice} className="forget-device-btn">
                                    Forget
                                </button>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Admin Password</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={credentials.password}
                                onChange={(e) => {
                                    setCredentials(prev => ({ ...prev, password: e.target.value }));
                                    setError("");
                                }}
                                required
                                placeholder="Enter admin password"
                                autoComplete="current-password"
                            />
                        </div>

                        {success && (
                            <motion.div
                                className="success-message"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}>
                                {success}
                            </motion.div>
                        )}

                        {error && (
                            <motion.div
                                className="error-message"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}>
                                {error}
                            </motion.div>
                        )}

                        <button
                            type="submit"
                            className="login-btn"
                            disabled={isLoading}>
                            {isLoading ? "Signing In..." : "Sign In"}
                        </button>
                    </form>
                )}

                {/* Security notice */}
                <div className="security-notice">
                    <span>🔒</span>
                    <span>Multi-factor authentication enabled</span>
                </div>
            </motion.div>
        </div>
    );
};

export default AdminLogin;
